/**
 * Unified metadata extraction for bubbles.
 * Handles both live streaming and historical reconstruction paths.
 */

import type { StreamAction } from "../streaming/reducer";
import type { ContentBlock } from "../tools/schemas";
import { parsePlanTasks } from "../utils/todo-utils";
import { normalizeToolInput } from "./normalizer";

/**
 * Source data for metadata extraction.
 * Can contain content blocks (historical path) or stream actions (live path).
 */
export interface MetadataSource {
	/** SDK content block (historical reconstruction) */
	contentBlock?: ContentBlock;
	/** Stream action (live streaming) */
	streamAction?: StreamAction;
}

/**
 * Extracts metadata from content blocks or stream actions.
 * Provides unified logic for both live streaming and historical reconstruction.
 *
 * @param source - Source data containing either content blocks or stream actions
 * @returns Metadata object for the bubble
 */
export function extractBubbleMetadata(source: MetadataSource): Record<string, unknown> {
	const metadata: Record<string, unknown> = {};

	// Extract from content block (historical path)
	if (source.contentBlock) {
		const block = source.contentBlock;

		// Tool use metadata
		if (block.type === "tool_use") {
			metadata.toolName = block.name;
			metadata.toolId = block.id;
			metadata.toolInput = normalizeToolInput(block.name, block.input);
		}

		// Tool result metadata
		if (block.type === "tool_result") {
			metadata.toolId = block.tool_use_id;
			metadata.isError = block.is_error ?? null;
		}

		// Thinking metadata
		if (block.type === "thinking") {
			metadata.signature = block.signature ?? null;
		}

		// Plan metadata
		if (block.type === "plan") {
			metadata.tasks = parsePlanTasks(block.plan || "");
		}
	}

	// Extract from stream action (live path)
	if (source.streamAction) {
		const action = source.streamAction;

		// Thinking metadata
		if (action.type === "THINKING") {
			metadata.signature = "signature" in action ? (action.signature ?? null) : null;
		}

		// Tool result metadata
		if (action.type === "TOOL_RESULT") {
			metadata.isError = "isError" in action ? (action.isError ?? null) : null;
		}

		// Sandbox metadata (live only)
		if (action.type === "SANDBOX_CONNECTED") {
			if ("sandboxId" in action) metadata.sandboxId = action.sandboxId;
			if ("durationMs" in action) metadata.durationMs = action.durationMs;
		}
	}

	return metadata;
}
